import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Star, Plus, Facebook } from "lucide-react";

const EMPTY = { customer_name: "", rating: 5, text: "", source: "facebook", source_url: "", customer_avatar: "", created_at: "" };

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const moderate = async (id, approved) => {
    await api.put(`/admin/reviews/${id}`, null, { params: { approved } });
    toast.success(approved ? "Approvata" : "Rifiutata");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Eliminata");
    load();
  };

  const submitReview = async () => {
    if (!form.customer_name.trim() || !form.text.trim()) {
      toast.error("Nome e testo sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/reviews", {
        ...form,
        rating: Number(form.rating),
        created_at: form.created_at || undefined,
      });
      toast.success("Recensione aggiunta");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Moderazione</div>
          <h1 className="font-admin text-2xl font-bold">Recensioni</h1>
          <p className="text-xs text-xd-muted mt-1">{items.length} recensioni · appaiono nel carosello della homepage</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          data-testid="btn-new-review"
          className="btn-cta rounded-lg px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nuova recensione
        </button>
      </div>

      {showForm && (
        <div data-testid="review-form" className="mb-6 bg-white border border-xd-border rounded-xl p-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Nome cliente (es. Marco B.)"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              data-testid="rev-name"
              className="border border-xd-border rounded-md px-3 py-2 text-sm"
            />
            <div className="flex items-center gap-3 bg-slate-50 border border-xd-border rounded-md px-3 py-2">
              <span className="text-xs text-xd-muted">Stelle:</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  data-testid={`rev-star-${n}`}
                  className="hover:scale-110 transition-transform"
                  aria-label={`${n} stelle`}
                >
                  <Star className={`w-6 h-6 ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
              <span className="ml-auto text-xs text-xd-muted font-mono-tech">{form.rating}/5</span>
            </div>
            <textarea
              placeholder="Testo della recensione (copiala da Facebook)"
              rows={4}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              data-testid="rev-text"
              className="md:col-span-2 border border-xd-border rounded-md px-3 py-2 text-sm"
            />
            <div className="grid grid-cols-2 gap-3 md:col-span-2">
              <select
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                data-testid="rev-source"
                className="border border-xd-border rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="facebook">Facebook (badge blu)</option>
                <option value="trustpilot">Trustpilot (badge verde)</option>
                <option value="google">Google (badge rosso)</option>
                <option value="manual">Nessun badge</option>
              </select>
              <input
                type="date"
                value={form.created_at ? form.created_at.slice(0, 10) : ""}
                onChange={(e) => setForm({ ...form, created_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                data-testid="rev-date"
                className="border border-xd-border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <input
              type="url"
              placeholder="URL recensione originale (opzionale) — es. link diretto al post Facebook o Trustpilot"
              value={form.source_url}
              onChange={(e) => setForm({ ...form, source_url: e.target.value })}
              data-testid="rev-source-url"
              className="md:col-span-2 border border-xd-border rounded-md px-3 py-2 text-sm font-mono text-xs"
            />
            <input
              type="url"
              placeholder="URL foto profilo (opzionale) — se vuota mostra iniziale"
              value={form.customer_avatar}
              onChange={(e) => setForm({ ...form, customer_avatar: e.target.value })}
              data-testid="rev-avatar"
              className="md:col-span-2 border border-xd-border rounded-md px-3 py-2 text-sm font-mono text-xs"
            />
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button
              type="button"
              onClick={submitReview}
              disabled={saving}
              data-testid="rev-save"
              className="btn-cta rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva e pubblica"}
            </button>
            <button
              type="button"
              onClick={() => { setForm(EMPTY); setShowForm(false); }}
              className="text-sm text-xd-muted hover:text-xd-text"
            >
              Annulla
            </button>
            <span className="text-xs text-xd-muted ml-auto">La recensione appare subito nel carosello (approvata automaticamente).</span>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {items.length === 0 && <div className="text-xd-muted text-sm">Nessuna recensione ancora. Click su &quot;Nuova recensione&quot; per aggiungerne una.</div>}
        {items.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-white border border-xd-border rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="flex gap-0.5">
                    {Array.from({ length: r.rating || 0 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                  </div>
                  {r.source === "facebook" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-[#1877F2] rounded-full px-2 py-0.5">
                      <Facebook className="w-3 h-3" /> Facebook
                    </span>
                  )}
                </div>
                <p className="text-sm">{r.text}</p>
                <div className="text-xs text-xd-muted mt-2">
                  — {r.customer_name}
                  {r.created_at && <span className="ml-2 font-mono-tech text-[10px] uppercase tracking-widest">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${r.approved ? "bg-xd-success/10 text-xd-success" : "bg-amber-100 text-amber-700"}`}>{r.approved ? "Approvata" : "In attesa"}</span>
                <div className="flex gap-1.5">
                  {!r.approved && <button type="button" onClick={() => moderate(r.id, true)} data-testid={`approve-${r.id}`} title="Approva" className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-xd-success/10 hover:bg-xd-success hover:text-white text-xd-success border border-xd-success/20 transition-colors"><Check className="w-5 h-5" /></button>}
                  {r.approved && <button type="button" onClick={() => moderate(r.id, false)} title="Sospendi" className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-500 hover:text-white border border-amber-200 transition-colors"><X className="w-5 h-5" /></button>}
                  <button type="button" onClick={() => del(r.id)} data-testid={`delete-${r.id}`} title="Elimina" className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white border border-red-200 transition-colors"><X className="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
